import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";

const friendlyError = (code) => {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "incorrect email or password";
    case "auth/email-already-in-use":
      return "an account with this email already exists";
    case "auth/weak-password":
      return "password must be at least 6 characters";
    case "auth/invalid-email":
      return "please enter a valid email address";
    case "auth/popup-closed-by-user":
      return "google sign-in was cancelled";
    case "auth/popup-blocked":
      return "popup was blocked — please allow popups for this site";
    default:
      return "something went wrong. please try again";
  }
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError("");
    if (!email || !password) { setError("please fill in all fields"); return; }
    if (isSignUp && !displayName.trim()) { setError("please enter your name"); return; }
    setLoading(true);
    try {
      if (isSignUp) {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: displayName.trim() });
        await sendEmailVerification(cred.user).catch(() => {});
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      navigate("/app");
    } catch (err) {
      console.error("Auth error:", err.code, err.message);
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      navigate("/app");
    } catch (err) {
      console.error("Google auth error:", err.code, err.message);
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}>
      <div style={{ width: "100%", maxWidth: 300 }}>

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 40 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)" }} />
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--text)" }}>greenline</span>
        </div>

        {/* Mode label */}
        <div className="label" style={{ marginBottom: 20 }}>
          {isSignUp ? "create_account" : "sign_in"}
        </div>

        {/* Google */}
        <button
          className="btn"
          onClick={handleGoogle}
          disabled={loading}
          style={{ width: "100%", justifyContent: "center", marginBottom: 16 }}
        >
          <svg width="13" height="13" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          continue with google
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          <span className="mono" style={{ fontSize: 10, color: "var(--text-muted)" }}>or</span>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        </div>

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {isSignUp && (
            <div>
              <div className="label" style={{ marginBottom: 5 }}>name</div>
              <input
                placeholder="your name"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
            </div>
          )}
          <div>
            <div className="label" style={{ marginBottom: 5 }}>email</div>
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
            />
          </div>
          <div>
            <div className="label" style={{ marginBottom: 5 }}>password</div>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
            />
          </div>
        </div>

        {error && (
          <div className="badge badge-red" style={{ display: "block", marginTop: 12, fontSize: 11 }}>
            {error}
          </div>
        )}

        <button
          className="btn btn-green"
          onClick={handleSubmit}
          disabled={loading}
          style={{ width: "100%", marginTop: 16, fontSize: 12 }}
        >
          {loading ? "please wait…" : isSignUp ? "create account →" : "sign in →"}
        </button>

        <div style={{ marginTop: 20, textAlign: "center" }}>
          <span className="mono" style={{ fontSize: 10, color: "var(--text-muted)" }}>
            {isSignUp ? "already have an account? " : "no account? "}
          </span>
          <button
            onClick={() => { setIsSignUp(s => !s); setError(""); }}
            style={{
              background: "none", border: "none",
              fontFamily: "var(--mono)", fontSize: 10,
              color: "var(--green)", cursor: "pointer", padding: 0,
            }}
          >
            {isSignUp ? "sign in" : "sign up"}
          </button>
        </div>

      </div>
    </div>
  );
}
